import Container from '@mui/material/Container';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { PageHelmet } from '../../components';
import { PAGES_ANALYTICS_TITLES } from '../../utils/enums';
import {
  CreateCommissionPlan,
  CreateCommissionFooter,
  SelectCommissionPlan,
  SelectMembers,
  CreatePlanForm,
  OverrideDialog,
  UnassignedMembersDialog,
  CommissionTypeView,
  CommissionSelectProducts,
} from '../../features/commissions/components';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import {
  getActiveStep,
  getMemberEmails,
  getSelectedCommissionPlan,
} from '../../store/commissions/selectors';

import { commissionsActions } from '../../store/commissions/slice';
import { useCommissions } from '../../hooks/useCommissions';
import { getMembers } from '../../store/team/selectors';
import { TMember } from '../../types';
import { TAddCommissionPlanReq } from '../../types/requests';

const CreatePlanPage = () => {
  const { t } = useTranslation(['payroll', 'commissions']);

  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(getActiveStep);
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  const [membersWithExistingPlans, setMembersWithExistingPlans] = useState<Array<TMember>>([]);
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);
  const [isOpenEmptyDialog, setIsOpenEmptyDialog] = useState(false);

  const memberEmails = useAppSelector(getMemberEmails);
  const members = useAppSelector(getMembers);

  const steps = useMemo(() => [
    t('commissionType', { ns: 'commissions' }),
    selectedCommissionPlan?.type === 'Products' ? t('selectProducts', { ns: 'commissions' }) : t('planType', { ns: 'commissions' }),
    t('planDetails', { ns: 'commissions' }),
    t('selectMembers')], [selectedCommissionPlan]);

  const {
    preparePayload,
    prepareFixedCommissionData,
    prepareCommissionStepsData,
    prepareProductCommissionData,
    handlePlanMutation,
  } = useCommissions();

  const handleNext = async () => {
    if (activeStep !== 3) {
      dispatch(commissionsActions.setActiveStep(activeStep + 1));
      return;
    }

    const payload = preparePayload();
    let data;

    if (selectedCommissionPlan.type === 'Fixed') {
      data = prepareFixedCommissionData(payload);
    } else if (selectedCommissionPlan.type === 'Steps') {
      data = prepareCommissionStepsData(payload);
    } else {
      data = prepareProductCommissionData(payload);
    }

    // Check for existing commission plans before mutating
    const membersWithPlans = members.filter((member) => member.commissionPlan !== null && memberEmails.includes(member.email));

    if (membersWithPlans.length > 0) {
      setMembersWithExistingPlans(membersWithPlans);
      setIsOverrideDialogOpen(true); // Open the dialog if any member has an existing plan
    } else if (members.length === 0) {
      setIsOpenEmptyDialog(true);
    } else {
      await handlePlanMutation(data as Omit<TAddCommissionPlanReq['planData'], 'creator' & 'updatedBy'>);
    }
  };

  const handleBack = () => {
    dispatch(commissionsActions.setActiveStep(activeStep - 1));
  };

  const isNextBtnDisabled = useMemo(() => {
    const disabledTab = selectedCommissionPlan.type !== 'Products' ? 2 : 1;
    return isNextDisabled && disabledTab === activeStep;
  }, [isNextDisabled, activeStep]);

  return (
    <PageHelmet title={PAGES_ANALYTICS_TITLES.CREATE_COMMISSION}>
      <Container
        sx={{
          px: { xs: 1, lg: 2 },
        }}
        maxWidth={false}
      >
        {activeStep !== 0 && (
        <Grid container spacing={2}>
          <Grid size={{
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12,
          }}
          >
            <CreateCommissionPlan steps={steps} />
          </Grid>
        </Grid>
        )}
        {activeStep === 0 && <CommissionTypeView />}
        {activeStep === 1 && (
          selectedCommissionPlan.type !== 'Products' ? (
            <SelectCommissionPlan />
          ) : (
            <CommissionSelectProducts setIsNextDisabled={setIsNextDisabled} />
          )
        )}

        {activeStep === 2 && <CreatePlanForm setIsNextDisabled={setIsNextDisabled} />}
        {activeStep === 3 && <SelectMembers />}

        {activeStep > 0
        && (
        <CreateCommissionFooter
          isNextDisabled={isNextBtnDisabled}
          stepsLength={steps?.length || 0}
          handleNext={handleNext}
          handleBack={handleBack}
        />
        )}
        <OverrideDialog
          setIsOverrideDialogOpen={setIsOverrideDialogOpen}
          isOverrideDialogOpen={isOverrideDialogOpen}
          membersWithExistingPlans={membersWithExistingPlans}
        />
        <UnassignedMembersDialog
          isOpenDialog={isOpenEmptyDialog}
          setIsOpenDialog={setIsOpenEmptyDialog}
        />
      </Container>
    </PageHelmet>
  );
};
export default CreatePlanPage;
